import React from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Typography,
} from '@mui/material';
import { useApp } from '../../context/AppContext';
import { useThemeColors } from '../../hooks/useThemeColors';
import { isCorretorSemPontuacao } from '../../utils/corretorUtils';

function ListaCorretores({ mesSelecionado, anoSelecionado, filtroAtivo, filtroVGV }) {
  const { corretores } = useApp();
  const colors = useThemeColors();

  const formatarMoeda = (valor) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor);
  };

  // Filtrar corretores - sempre mostrar apenas ativos no dashboard e elegíveis para ranqueamento
  let corretoresFiltrados = corretores.filter(c => {
    // Sempre filtrar apenas corretores ativos no dashboard e que pontuam
    if (!c.ativo || isCorretorSemPontuacao(c)) return false;
    // Se o filtro for "Não", não mostrar nada (já que só mostramos ativos)
    if (filtroAtivo === 'Não') return false;
    return true;
  });

        // Helper function to get venda value (backward compatible)
        const getVendaValor = (corretor, ano, mes) => {
          const venda = corretor.vendas?.[ano]?.[mes];
          if (!venda) return 0;
          // Support both old format (number) and new format (object)
          return typeof venda === 'object' ? (venda.valor || 0) : venda;
        };

        // Helper function to get agenciamento value (integer)
        const getAgenciamento = (corretor, ano, mes) => {
          const venda = corretor.vendas?.[ano]?.[mes];
          if (!venda) return 0;
          // Support both old format (number) and new format (object)
          const agenciamento = typeof venda === 'object' ? (venda.agenciamento || 0) : 0;
          return parseInt(agenciamento, 10); // Ensure it's an integer
        };

        // Helper function to calculate total VGV for all months in a year
        const getVGVTotalAno = (corretor, ano) => {
          if (!corretor.vendas?.[ano]) return 0;
          return Object.values(corretor.vendas[ano]).reduce((total, venda) => {
            const valor = typeof venda === 'object' ? (venda.valor || 0) : venda;
            return total + valor;
          }, 0);
        };

        // Helper function to calculate total agenciamento for all months in a year
        const getAgenciamentoTotalAno = (corretor, ano) => {
          if (!corretor.vendas?.[ano]) return 0;
          return Object.values(corretor.vendas[ano]).reduce((total, venda) => {
            const agenciamento = typeof venda === 'object' ? (venda.agenciamento || 0) : 0;
            return total + parseInt(agenciamento, 10);
          }, 0);
        };

        // Mapear com VGV e agenciamento
        corretoresFiltrados = corretoresFiltrados.map(corretor => {
          let vgv = 0;
          let agenciamento = 0;
          
          if (mesSelecionado === 'Todos') {
            // If "Todos" is selected, sum all months of the selected year
            vgv = getVGVTotalAno(corretor, anoSelecionado);
            agenciamento = getAgenciamentoTotalAno(corretor, anoSelecionado);
          } else {
            // Otherwise, get the specific month
            vgv = getVendaValor(corretor, anoSelecionado, mesSelecionado);
            agenciamento = getAgenciamento(corretor, anoSelecionado, mesSelecionado);
          }
          
          return { ...corretor, vgv, agenciamento };
        });

  // Filtrar corretores com VGV = 0 (não devem aparecer na lista)
  corretoresFiltrados = corretoresFiltrados.filter(c => c.vgv > 0);

  // Filtrar por VGV se necessário
  if (filtroVGV !== 'Todos') {
    const valorMinimo = parseFloat(filtroVGV);
    corretoresFiltrados = corretoresFiltrados.filter(c => c.vgv >= valorMinimo);
  }

  // Ordenar por VGV (maior para menor)
  corretoresFiltrados.sort((a, b) => b.vgv - a.vgv);

  // Calcular altura máxima para mostrar no máximo 13 corretores
  // Cada linha tem aproximadamente 48px (com padding)
  const alturaLinha = 48;
  const maxLinhasVisiveis = 13; // Máximo de 13 corretores visíveis
  const alturaMaxima = alturaLinha * maxLinhasVisiveis;

  return (
    <Box
      sx={{
        backgroundColor: '#1D1D1D',
        borderRadius: 2,
        p: 2,
        border: `1px solid ${colors.border.primary}`,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Typography
        variant="h6"
        sx={{
          color: 'text.primary',
          fontWeight: 'bold',
          mb: 1,
          fontSize: '1rem',
          flexShrink: 0,
        }}
      >
        Lista de corretores
      </Typography>
      <TableContainer 
        sx={{ 
          flex: 1, 
          overflowY: 'auto', 
          overflowX: 'hidden',
          minHeight: 0,
          maxHeight: alturaMaxima,
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'rgba(0, 0, 0, 0.1)',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#BEBEBE',
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: colors.secondary,
            },
          },
        }}
      >
        <Table size="small" >
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: 'text.secondary', fontWeight: 'bold', py: 1, fontSize: '0.875rem', paddingRight: '4px' }}>
                Foto
              </TableCell>
              <TableCell sx={{ color: 'text.secondary', fontWeight: 'bold', py: 1, fontSize: '0.875rem', paddingLeft: '4px', paddingRight: '4px' }}>
                Nome
              </TableCell>
              <TableCell
                align="right"
                sx={{ color: 'text.secondary', fontWeight: 'bold', py: 1, fontSize: '0.875rem', paddingLeft: '4px' }}
              >
                VGV {mesSelecionado}
              </TableCell>
              <TableCell
                align="right"
                sx={{ color: 'text.secondary', fontWeight: 'bold', py: 1, fontSize: '0.7rem', paddingRight: '8px' }}
              >
                Ag.
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {corretoresFiltrados.map((corretor, index) => (
              <TableRow
                key={corretor.id}
                hover
                sx={{
                  '&:hover': {
                    backgroundColor: colors.hover.light,
                  },
                }}
              >
                <TableCell sx={{ py: 0.5, paddingRight: '4px' }}>
                  <Avatar 
                    src={corretor.foto || undefined} 
                    alt={corretor.nome} 
                    sx={{ width: 32, height: 32 }}
                  >
                    {!corretor.foto && corretor.nome 
                      ? corretor.nome.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()
                      : ''}
                  </Avatar>
                </TableCell>
                <TableCell sx={{ color: '#EBEBE9', py: 0.5, fontSize: '0.875rem', paddingLeft: '4px', paddingRight: '4px' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 22,
                        height: 22,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.72rem',
                        fontWeight: 'bold',
                        flexShrink: 0,
                        backgroundColor: 'rgba(255, 255, 255, 0.08)',
                        color: '#EBEBE9',
                        border: '1px solid rgba(255, 255, 255, 0.15)',
                      }}
                    >
                      {index + 1}
                    </Box>
                    <Typography
                      variant="body2"
                      noWrap
                      sx={{
                        fontWeight: index < 3 ? 700 : 400,
                        fontSize: '0.875rem',
                        color: '#EBEBE9',
                      }}
                    >
                      {corretor.nome}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ color: '#EBEBE9', fontWeight: 'bold', py: 0.5, fontSize: '0.875rem', paddingLeft: '4px' }}
                >
                  {formatarMoeda(corretor.vgv)}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ color: '#EBEBE9', py: 0.5, fontSize: '0.8rem', paddingRight: '8px' }}
                >
                  {corretor.agenciamento || 0}
                </TableCell>
              </TableRow>
            ))}
            {corretoresFiltrados.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography
                    variant="body2"
                    sx={{ color: 'text.secondary', py: 2 }}
                  >
                    Nenhum corretor encontrado
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default ListaCorretores;
